/**
 * Demo mode — bypass Supabase entirely with a synthetic-or-real seed.
 *
 * Three priority sources for the seed (in order):
 *   1. window.DEMO_SEED_INLINE  — set by the bundler for the shareable HTML
 *   2. fetch("./demo-seed.json") — localhost dev workflow
 *   3. synthetic algorithmic seed — production fallback (button is hidden
 *      anyway in production via IS_LOCALHOST gating, but defence in depth)
 *
 * lastSyncedSnapshot stays null so saveStore never tries to push.
 */
import { isDemoMode, patchState } from "../state/store";
import { clearSyncBaseline } from "../state/storage";
import {
  ADMIN_NAME,
  type DemoSeed,
  type RosterMap,
  type CalendarMap,
  type MeetingMap,
  type NoteMap,
  type StatusCode,
} from "../types";
import { activePeople, bySurname, nameToEmail } from "../utils/names";
import { ALLOWED_EMAIL_DOMAIN } from "../config";
import { dateKey, daysInMonth } from "../utils/dates";

declare global {
  interface Window {
    DEMO_SEED_INLINE?: DemoSeed;
  }
}

async function loadSeed(): Promise<DemoSeed | null> {
  if (typeof window !== "undefined" && window.DEMO_SEED_INLINE) {
    return window.DEMO_SEED_INLINE;
  }
  try {
    const r = await fetch("./demo-seed.json", { cache: "no-store" });
    if (!r.ok) return null;
    return (await r.json()) as DemoSeed;
  } catch {
    return null;
  }
}

function syntheticSeed(): DemoSeed {
  // Tiny synthesised roster using non-real placeholder names. Demonstrates
  // the UI but never leaks real employees.
  const placeholders = [
    ["Anna", "Test"], ["Bart", "Test"], ["Cara", "Demo"], ["Dario", "Demo"],
    ["Elena", "Test"], ["Filippo", "Demo"], ["Greta", "Test"], ["Hugo", "Demo"],
    ["Iris", "Test"], ["Julia", "Demo"], ["Kai", "Test"], ["Luna", "Demo"],
  ];
  const roster: RosterMap = {};
  placeholders.forEach(([first = "", last = ""], i) => {
    const name = `${first} ${last}`;
    roster[name] = {
      unit: ["Engineering", "Design", "Operations", "Finance"][i % 4] ?? "Operations",
      role: ["Officer", "Senior officer", "Head of unit", "Junior officer"][i % 4] ?? "Officer",
      email: nameToEmail(name, ALLOWED_EMAIL_DOMAIN),
      active: true,
      first_name: first,
      last_name: last,
      last_active_day: null,
    };
  });

  const today = new Date();
  const cal: CalendarMap = {};
  const notes: NoteMap = {};
  const mtg: MeetingMap = {};
  const hash = (s: string): number => {
    let h = 2166136261;
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = (h * 16777619) >>> 0;
    }
    return h;
  };
  // The demo seed only paints P/T/R/M/O (the original 5 codes the previous
  // version shipped with). The 6th status (A — "available but not at desk")
  // is intentionally left out of the synthetic stream so the demo's pattern
  // distribution stays unchanged after migration 16; users still see A as a
  // pickable option in the modal.
  const codes: StatusCode[] = ["P", "T", "R", "M", "O"];
  for (let off = -3; off <= 3; off++) {
    const dt = new Date(today.getFullYear(), today.getMonth() + off, 1);
    const y = dt.getFullYear();
    const m = dt.getMonth();
    const n = daysInMonth(y, m);
    Object.keys(roster).forEach((name, idx) => {
      for (let d = 1; d <= n; d++) {
        const wd = new Date(y, m, d).getDay();
        if (wd === 0 || wd === 6) continue;
        const k = dateKey(y, m, d);
        const r = hash(name + k) % 100;
        const bias = idx % 4;
        let status: StatusCode;
        if (r < 3) status = codes[3]!; // M
        else if (r < 8) status = codes[1]!; // T
        else if (r < 13) status = codes[4]!; // O
        else status = r < (bias === 0 ? 65 : bias === 1 ? 50 : bias === 2 ? 30 : 45) ? "P" : "R";
        (cal[name] = cal[name] ?? {})[k] = status;
      }
    });
  }
  return { roster, calendar: cal, notes, meetings: mtg };
}

export async function enterDemoMode(): Promise<void> {
  isDemoMode.value = true;
  const seed = (await loadSeed()) ?? syntheticSeed();

  // Build profilesByRoster so the Directory's Access column has rows.
  // Demo demonstrates all three role tiers:
  //   index 0       → super admin (gold pill)
  //   index 1..5    → plain admin (blue pill)
  //   index 6..end  → user (gray pill)
  const profilesByRoster: Record<string, {
    id: string; email: string; roster_name: string;
    is_admin: boolean; is_super_admin: boolean; password_set: boolean;
  }> = {};
  const active = activePeople(seed.roster);
  active.forEach((name, i) => {
    const isSuper = i === 0;
    const isAdmin = i < 6 || isSuper;  // super implies admin
    profilesByRoster[name] = {
      id: `demo-${name.toLowerCase().replace(/\W+/g, "-")}`,
      email: seed.roster[name]?.email ?? "",
      roster_name: name,
      is_admin: isAdmin,
      is_super_admin: isSuper,
      password_set: true,
    };
  });

  patchState({
    roster: seed.roster,
    calendar: seed.calendar,
    notes: seed.notes,
    meetings: seed.meetings,
    isAdmin: true,
    // Demo viewer is the top tier so the role-toggle column is exercised.
    isSuperAdmin: true,
    currentUser: ADMIN_NAME,
    actAs: active.sort(bySurname)[0] ?? null,
    profileId: "demo-self",
    adminEmail: "demo-admin@example.invalid",
    profilesByRoster,
    // Demo closures: a future bank holiday + a past one so the banner +
    // the past-section in the admin panel both have something to render.
    closures: (() => {
      const t = new Date();
      const future = new Date(t); future.setDate(t.getDate() + 5);
      const past = new Date(t); past.setDate(t.getDate() - 30);
      return {
        [future.toISOString().slice(0, 10)]: "Demo upcoming closure",
        [past.toISOString().slice(0, 10)]: "Demo past closure",
      };
    })(),
  });

  clearSyncBaseline();
}
